import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, Optional, Output} from "@angular/core";
import {
    EntityData,
    LocalisableEntityData,
    ReportModel,
    SavedReportData,
    ValidationService
} from "@fedeltapointofsale/data-client";
import {
    FUIAlertActions,
    FUIAlertService,
    FUIAlertType,
    FUIFormationService,
    FUISpinnersService,
    FUIStackedPageContextService,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {TranslateService} from "@ngx-translate/core";
import {ErrorHandlerService} from "../../../../error-handler.service";
import {EditService} from "../../edit/edit-page/edit.service";
import {EntityFormBuilder} from "../../edit/entity-form-builder/entity-form-builder";
import {AbstractDataPageComponent} from "../../abstract-data-page.component";
import {
    CustomerSupportMessagingService
} from "../../../../customer-support-messaging/customer-support-messaging.service";
import {ReportGenerationService} from "../report-generation.service";
import {ReportParams} from "./report-params";
import {Observable} from "rxjs";
import {EditParams} from "../../edit/edit-page/edit-params";
import {EditMode} from "../../edit/edit-page/edit-mode";
import {MyreportEditPageComponent} from "../../../../reports/myreport/myreport-edit-page/myreport-edit-page.component";
import {MyreportData} from "../../../../reports/myreport/myreport.data";

@Component({
    selector: "wbo-report-page",
    templateUrl: "./report-page.component.html",
    styleUrls: ["report-page.component.scss"],
    providers: [EditService, FUIFormationService, ReportGenerationService]
})
export class ReportPageComponent extends AbstractDataPageComponent {

    public static PARAM_MY_REPORT_ID = "SAVED_REPORT_ID";

    @Input() myReportPostFix: string;

    @Output() onLoadedSavedReport: EventEmitter<any> = new EventEmitter<any>();

    savedReport: SavedReportData;

    constructor(public reportModel: ReportModel<any>,
                validationService: ValidationService,
                editService: EditService,
                changeDetectorRef: ChangeDetectorRef,
                entityFormBuilder: EntityFormBuilder,
                formationService: FUIFormationService,
                alertService: FUIAlertService,
                translate: TranslateService,
                errorHandlerService: ErrorHandlerService,
                spinnersService: FUISpinnersService,
                public customerSupportMessagingService: CustomerSupportMessagingService,
                private stackedPageService: FUIStackedPageService,
                @Optional() stackedPageContextService: FUIStackedPageContextService,
                @Inject(ValidationService.APPLICATION_KEY) application: string,
                elementRef: ElementRef,
                private reportGenerationService: ReportGenerationService) {
        super(reportModel, validationService, editService, changeDetectorRef, entityFormBuilder, formationService, alertService, translate, spinnersService,
            errorHandlerService, stackedPageContextService, application, elementRef);

        this.titleLabel = "wbo." + this.reportModel.getResource() + ".report.title";

        if (this.reportModel.isEntityLocalisable()) {
            (<LocalisableEntityData<any>>this.reportModel.data).localisableContext.universal = true;
        }

        if (stackedPageContextService && stackedPageContextService.params[ReportPageComponent.PARAM_MY_REPORT_ID]) {
            this.reportModel.getSavedReportParameters(stackedPageContextService.params[ReportPageComponent.PARAM_MY_REPORT_ID]).then((savedReport) => {
                if (savedReport) {
                    this.savedReport = savedReport;
                    this.reportModel.data.assign(savedReport.params);

                    this.onLoadedSavedReport.emit();
                }
            });
        } else {
            this.loadDefaultSavedReport();
        }
    }

    get descriptionLabel(): string {
        return "wbo." + this.reportModel.getResource() + ".report.description";
    }

    loadDefaultSavedReport(): void {
        this.savedReport = undefined;
        this.reportModel.getDefaultSavedReportParameters().then((savedReport) => {
            if (savedReport) {
                this.savedReport = savedReport;
                this.reportModel.data.assign(savedReport.params);
            }
        });
    }

    protected execute(): Promise<Boolean> {
        return this.reportGenerationService.execute(this.reportModel);
    }

    cancel(): void {
        this.closeStackedPage().then((canClose) => {
            if (canClose) {
                this.stackedPageContextService.cancel();
            }
        });
    }

    get running(): Observable<boolean> {
        return this.reportGenerationService.running$;
    }

    get reportParams(): any {
        if (this.stackedPageContextService) {
            return ReportParams.isInstance(this.stackedPageContextService.params) ? this.stackedPageContextService.params.params : undefined;
        } else {
            return undefined;
        }
    }

    saveMyReport(): void {
        if (this.savedReport) {
            this.alertService.show({
                title: "wbo.report.prompt.saveMyReport.heading",
                message: "wbo.report.prompt.saveMyReport.message",
                params: {name: this.savedReport.name},
                type: FUIAlertType.CONFIRMATION,
                actions: [
                    FUIAlertActions.CANCEL_ACTION,
                    {
                        name: "override",
                        label: "wbo.report.button.override"
                    }, {
                        name: "createNew",
                        label: "wbo.report.button.createNew"
                    }
                ]
            }).subscribe((action) => {
                if (action.name === "createNew") {
                    this.createNewMyReport();
                } else if (action.name === "override") {
                    this.editMyReport();
                }
            });
            return;
        } else {
            this.createNewMyReport();
        }
    }

    createNewMyReport(): void {
        const reportParameters: string = this.reportModel.data.toJSONString();

        const reportId: string = this.myReportPostFix ? MyreportData.resourceNameToUuid(this.reportModel.getResource()) + "_" + this.myReportPostFix :
            MyreportData.resourceNameToUuid(this.reportModel.getResource());

        this.subscriptions.add(this.stackedPageService
            .open(MyreportEditPageComponent, new EditParams(undefined, EditMode.ADD, undefined,
                {
                    [MyreportEditPageComponent.EDIT_PARAM_REPORT_PARAMS]: reportParameters,
                    [MyreportEditPageComponent.EDIT_PARAM_REPORT_ID]: reportId,
                    [MyreportEditPageComponent.EDIT_PARAM_REPORT_TITLE]: this.title ? "'" + this.title + "'" : this.titleLabel
                }
            ))
            .subscribe((entity: EntityData<any>) => {
            })
        );
    }

    editMyReport(): void {
        const reportParameters: string = this.reportModel.data.toJSONString();
        this.subscriptions.add(this.stackedPageService
            .open(MyreportEditPageComponent, new EditParams(this.savedReport.id, EditMode.EDIT, undefined,
                {
                    [MyreportEditPageComponent.EDIT_PARAM_REPORT_PARAMS]: reportParameters,
                    [MyreportEditPageComponent.EDIT_PARAM_REPORT_TITLE]: this.title || this.titleLabel
                }
            ))
            .subscribe((entity: EntityData<any>) => {
            })
        );
    }

    override shouldCheckDiscardChanges(): boolean {
        return false;
    }
}
